import React, { useEffect, useState, useContext } from 'react'
import { useHistory, useLocation } from 'react-router'
import axios from 'axios'
import queryString from 'query-string'
import FlatPagination from 'material-ui-flat-pagination'
import moment from 'moment-timezone'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import store from '../store'
import { setSearchQuery, unsetSearchQuery } from '../store/actions/searchQuery'
import { setDialog } from '../store/actions/dialog'

import {
  Grid,
  Paper,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Badge
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import CancelIcon from '@material-ui/icons/Close'
import Title from '../components/atoms/Title'
import SelectInputTableHead from '../components/atoms/SelectInputTableHead'
import DatePeriodFragment from '../components/molecules/DatePeriodFragment'
import WebViewModal from '../components/organisms/WebViewModal'
import ConfigContext from '../ConfigContext'
import { Event } from '../types/Event'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflowY: 'hidden',
    flexDirection: 'column',
    height: `100%`
  },
  tableBody: {
    '& .MuiTableCell-sizeSmall': {
      padding: '4px 7px 4px 7px'
    }
  },
  tableHead: {
    '& .MuiTableCell-sizeSmall': {
      padding: '4px 7px 4px 7px'
    }
  },
  badge: {
    '& .MuiBadge-badge': {
      height: 'auto',
      padding: 3,
      borderRadius: '50%',
      backgroundColor: '#FF6363',
      color: '#fff'
    }
  }
}))

type QueryString = {
  sort?: string
  order?: 'asc' | 'desc'
  page?: string
  limit?: string
  sender?: string
  close_flg?: string
  post?: string
  progress?: string
}

const Events: React.FC<
  ReturnType<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>
> = ({ setDialog, setSearchQuery, unsetSearchQuery, searchQuery }) => {
  const fetchLimit = 100
  const history = useHistory()
  const location = useLocation()
  const initQuery: QueryString = queryString.parse(location.search)
  const [total, setTotal] = useState<number>(0)
  const [limit, setLimit] = useState<number>(fetchLimit)
  const [events, setEvents] = useState<Event[]>([])
  const [modalState, setModalState] = useState(false)
  const [webViewUrl, setWebViewUrl] = useState('')
  const [sender, setSender] = useState<{ sender: string }[]>([])
  const [keyword, setKeyword] = useState<{
    sender: string
    close_flg: string
    post: string
    progress: string
  }>({
    sender: '',
    close_flg: '',
    post: '',
    progress: ''
  })
  const fetchParams = {
    sort: ['start_date', 'id'],
    order: ['desc', 'desc'],
    limit: fetchLimit
  }
  const config = useContext(ConfigContext)

  const fetchData = React.useCallback(async (params: QueryString) => {
    const { data } = await axios.get<{
      number_of_hits: number
      rows: Event[]
    }>('/events', {
      params: {
        ...fetchParams,
        ...params
      }
    })
    setEvents(data.rows)
    setTotal(data.number_of_hits)
    if (data.number_of_hits > 0) {
      setLimit(fetchLimit)
    } else {
      setLimit(0)
    }
  }, [])

  const fetchSender = async () => {
    const { data } = await axios.get<{ sender: string }[]>('/events/sender')
    setSender(data)
  }

  useEffect(() => {
    fetchSender()
    if (
      searchQuery.page ||
      searchQuery.sender ||
      searchQuery.close_flg ||
      searchQuery.post ||
      searchQuery.progress
    ) {
      const query: QueryString = {
        page: searchQuery.page,
        sender: searchQuery.sender,
        close_flg: searchQuery.close_flg,
        post: searchQuery.post,
        progress: searchQuery.progress
      }
      const urlQuery: QueryString = query
      for (let key in urlQuery) {
        if (!urlQuery[key]) {
          delete urlQuery[key]
        }
      }
      history.push({
        pathname: '/events',
        search: queryString.stringify(urlQuery)
      })
      setKeyword({ ...searchQuery })
      fetchData(searchQuery)
    } else {
      initQuery.page = '1'
      fetchData(initQuery)
    }
    unsetSearchQuery()
  }, [])

  const scrollPageTop = () =>
    document.getElementById('main-content').scroll({
      top: 0,
      left: 0
    })

  const handlePageChange = async (page: number) => {
    try {
      fetchSender()
      const query: QueryString = {
        ...initQuery,
        page: String(page)
      }
      history.push({
        pathname: '/events',
        search: queryString.stringify(query)
      })
      scrollPageTop()
      await fetchData(query)
    } catch (error) {
      alert(error.message)
    }
  }

  const handleSearch = async (key: string, select: string) => {
    try {
      const query: QueryString = {
        ...keyword,
        [key]: select,
        page: '1'
      }
      const urlQuery: QueryString = query
      for (let key in urlQuery) {
        if (!urlQuery[key]) {
          delete urlQuery[key]
        }
      }
      history.push({
        pathname: '/events',
        search: queryString.stringify(urlQuery)
      })
      setKeyword({ ...keyword, [key]: select })
      fetchSender()
      await fetchData(query)
    } catch (error) {
      alert(error.message)
    }
  }

  const handleFilterReset = async () => {
    try {
      fetchSender()
      setKeyword({
        sender: '',
        close_flg: '',
        post: '',
        progress: ''
      })
      history.push({
        pathname: '/events'
      })
      const query: QueryString = {
        page: '1'
      }
      await fetchData(query)
    } catch (error) {
      alert(error.message)
    }
  }

  const currentPageCaption = () => {
    const totalNumber = total
    const currentPage = Number(initQuery.page) || 1
    const totalPage = Math.ceil(total / limit)
    const startNumber = totalNumber == 0 ? 0 : (currentPage - 1) * limit + 1
    const lastNumber = currentPage === totalPage ? totalNumber : currentPage * limit
    return (
      <Typography variant="caption" align="center" display="block" color="primary">
        全 {totalNumber} 件｜ ({startNumber} - {lastNumber}件)
      </Typography>
    )
  }

  const handleCheckBoxChange = async (eventId: number, key: string, checked: boolean) => {
    try {
      const { data } = await axios.put<Event>(`/events/${eventId}`, {
        [key]: checked
      })
      const _events = events.slice()
      events.forEach((event, index) => {
        if (event.id === eventId) {
          _events[index] = data
        }
      })
      setEvents(_events)
    } catch (error) {
      alert(error.message)
    }
  }

  const handleDelete = async (eventId: number) => {
    try {
      setDialog(
        '確認',
        '削除してもよろしいですか？',
        async () => {
          await axios.delete(`/events/${eventId}`)
          initQuery.page = initQuery.page || String(1)
          fetchData(initQuery)
        },
        () => {}
      )
    } catch (error) {
      alert(error.message)
    }
  }

  const classes = useStyles({})
  return (
    <React.Fragment>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Title>イベント一覧</Title>

          <Box mb={5} display="flex" justifyContent="space-between" alignItems="center" mr={2}>
            <Button variant="contained" color="primary" href="/create-event">
              イベントの新規作成
            </Button>
            {config.enableEventsFilterReset === '1' && (
              <Badge
                className={classes.badge}
                badgeContent={<CancelIcon fontSize="small" />}
                invisible={Object.values(keyword).find(k => k !== '') ? false : true}
                max={0}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleFilterReset}
                  disabled={Object.values(keyword).find(k => k !== '') ? false : true}
                >
                  フィルターを解除
                </Button>
              </Badge>
            )}
          </Box>

          <Box my={4}>
            <Box display="flex" justifyContent="center">
              {currentPageCaption}
            </Box>
            <Box display="flex" justifyContent="center">
              <FlatPagination
                limit={limit}
                offset={(Number(initQuery.page || 1) - 1) * limit}
                total={total}
                onClick={(ev, offset, _page) => {
                  handlePageChange(_page)
                }}
              />
            </Box>
          </Box>

          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell align="center">
                  {config.enableEventsFilterPost === '1' ? (
                    <SelectInputTableHead
                      value={keyword.post}
                      label={'配信状況'}
                      selectHandler={(select: string) => handleSearch('post', select)}
                      options={[
                        { value: '', label: 'すべて表示' },
                        { value: '0', label: '配信中' },
                        { value: '1', label: '配信時間外' },
                        { value: '2', label: '配信停止中' }
                      ]}
                    />
                  ) : (
                    '配信状況'
                  )}
                </TableCell>
                <TableCell align="center">
                  {config.enableEventsFilterProgress === '1' ? (
                    <SelectInputTableHead
                      value={keyword.progress}
                      label={'ステータス'}
                      selectHandler={(select: string) => handleSearch('progress', select)}
                      options={[
                        { value: '', label: 'すべて表示' },
                        { value: '0', label: '開催前' },
                        { value: '1', label: '開催中' },
                        { value: '2', label: '開催終了' }
                      ]}
                    />
                  ) : (
                    'ステータス'
                  )}
                </TableCell>
                <TableCell>イベント名</TableCell>
                <TableCell>
                  {config.enableEventsFilterSender === '1' ? (
                    <SelectInputTableHead
                      value={keyword.sender}
                      label={'発信者'}
                      selectHandler={(select: string) => handleSearch('sender', select)}
                      options={[{ value: '', label: 'すべて表示' }].concat(
                        sender.map(d => {
                          return {
                            value: d.sender,
                            label: d.sender
                          }
                        })
                      )}
                    />
                  ) : (
                    '発信者'
                  )}
                </TableCell>
                <TableCell align="center">掲載期間</TableCell>
                <TableCell align="center">イベント期間</TableCell>
                <TableCell align="center">
                  {config.enableEventsFilterCloseFlg === '1' ? (
                    <SelectInputTableHead
                      value={keyword.close_flg}
                      label={'表示/非表示'}
                      selectHandler={(select: string) => handleSearch('close_flg', select)}
                      options={[
                        { value: '', label: 'すべて表示' },
                        { value: '0', label: '表示中' },
                        { value: '1', label: '非表示' }
                      ]}
                    />
                  ) : (
                    '表示/非表示'
                  )}
                </TableCell>
                <TableCell align="center">編集</TableCell>
                <TableCell align="center">削除</TableCell>
                <TableCell align="center">プレビュー</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody}>
              {events.map((event, index) => (
                <TableRow key={event.id}>
                  <TableCell align="center">
                    {(() => {
                      if (event.close_flg) {
                        return <Chip label="配信停止中" color="default" />
                      } else if (
                        moment() < moment(event.release_date) ||
                        moment() >= moment(event.close_date)
                      ) {
                        return <Chip label="配信期間外" color="default" />
                      } else {
                        return <Chip label="配信中" color="secondary" />
                      }
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    {(() => {
                      if (moment() < moment(event.start_date)) {
                        return (
                          <Chip
                            label="開催前"
                            style={{ backgroundColor: '#3399FF', color: '#fff' }}
                          />
                        )
                      } else if (moment().isAfter(moment(event.end_date), 'day')) {
                        return <Chip label="開催終了" color="default" />
                      } else {
                        return <Chip label="開催中" color="secondary" />
                      }
                    })()}
                  </TableCell>
                  <TableCell align="left">
                    <Typography variant="subtitle2">{event.title}</Typography>
                  </TableCell>
                  <TableCell align="left">{event.sender}</TableCell>
                  <TableCell align="center">
                    <DatePeriodFragment startTime={event.release_date} endTime={event.close_date} />
                  </TableCell>
                  <TableCell align="center">
                    <DatePeriodFragment
                      startTime={event.start_date}
                      endTime={event.end_date}
                      removeTime={Boolean(event.all_day_flg)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color={!Boolean(event.close_flg) ? 'primary' : 'default'}
                      onClick={() => {
                        handleCheckBoxChange(event.id, 'close_flg', !event.close_flg)
                      }}
                    >
                      {!Boolean(event.close_flg) ? '表示中' : '非表示'}
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="primary"
                      href={`edit-event/${event.id}`}
                      onClick={() => {
                        setSearchQuery(
                          initQuery.page,
                          initQuery.sender,
                          initQuery.close_flg,
                          initQuery.post,
                          initQuery.progress
                        )
                      }}
                    >
                      編集
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleDelete(event.id)}
                    >
                      削除
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        if (event.external_link) {
                          alert(
                            '外部サイトへのリンクが設置されています。\nリンク先の確認は「編集」よりお願いします。'
                          )
                        } else if (event.file_link) {
                          alert(
                            'ファイルリンクが設置されています。\nファイルの確認は「編集」よりお願いします。'
                          )
                        } else {
                          setWebViewUrl(`${axios.defaults.baseURL}/previews/events/${event.id}`)
                          setModalState(true)
                        }
                      }}
                    >
                      プレビュー
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box my={4}>
            <Box display="flex" justifyContent="center">
              {currentPageCaption}
            </Box>
            <Box display="flex" justifyContent="center">
              <FlatPagination
                limit={limit}
                offset={(Number(initQuery.page || 1) - 1) * limit}
                total={total}
                onClick={(ev, offset, _page) => {
                  handlePageChange(_page)
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Grid>
      <WebViewModal isOpen={modalState} handleClose={() => setModalState(false)} url={webViewUrl} />
    </React.Fragment>
  )
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    setDialog: (title: string, content: string, okHandler: () => void, ngHandler: () => void) =>
      dispatch(setDialog(title, content, okHandler, ngHandler)),
    setSearchQuery: (
      page: string,
      sender: string,
      close_flg: string,
      post: string,
      progress: string
    ) => dispatch(setSearchQuery(page, sender, '', '', '', close_flg, post, '', progress)),
    unsetSearchQuery: () => dispatch(unsetSearchQuery())
  }
}

const mapStateToProps = (state: ReturnType<typeof store.getState>) => {
  return {
    searchQuery: state.searchQuery
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Events)
